import { Dispatch, FC, SetStateAction, useEffect, useRef } from "react";
import { CellEditRequestEvent, SelectionChangedEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { TableWrapper } from "../../../common/TableWithButtonsAboveLayout/TableWithButtonsAboveLayout.styles";
import Table from "../../../components/Table/Table";
import { EditCellRenderer } from "../../../components/Table/EditCellRenderer/EditCellRenderer";
import { RawProd } from "../../../services/rawProd/rawProd.types";
import { useGetRawProducts } from "../../../services/rawProd/rawProd.service";
import { useFetchErrorToast } from "../../../utils/useFetchErrorToasts/useFetchErrorToast";

export interface RawProductUsage {
  rawProductId: number;
  rawProductDescription: string;
  usageAmount: number;
}

interface EditRecipeDetailsTablesProps {
  selectedRawProducts: RawProductUsage[];
  setSelectedRawProducts: Dispatch<SetStateAction<RawProductUsage[]>>;
}

const EditRecipeDetailsTables: FC<EditRecipeDetailsTablesProps> = (
    {
      selectedRawProducts,
      setSelectedRawProducts
    }
) => {
  const {
    data: getRawProductsResponse,
    isError: getRawProductsIsError,
    error: getRawProductsError
  } = useGetRawProducts();
  const rawProducts: RawProd[] = getRawProductsResponse?.data ?? [];

  useFetchErrorToast([
    {
      label: 'Error fetching Raw Products',
      id: 'rawProductsFetchError',
      isError: getRawProductsIsError,
      error: getRawProductsError
    }
  ]);

  const availableGridRef = useRef<AgGridReact>(null);

  useEffect(() => {
    availableGridRef.current?.api?.forEachNode((node) => {
      node.setSelected(
          selectedRawProducts.some(
              (selectedRawProduct) => selectedRawProduct.rawProductId === node.data?.moaRawProdNo
          )
      )
    })
  }, [selectedRawProducts, getRawProductsResponse?.data]);

  const onAvailableSelectionChanged = (
      event: SelectionChangedEvent
  ) => {
    //Remove deselected Raw Products
    setSelectedRawProducts((prevSelected) =>
        prevSelected.filter((rawProduct) =>
            event.api.getSelectedRows().some(
                (row => row.moaRawProdNo === rawProduct.rawProductId)
            )
        )
    );

    //Add selected Raw Products
    event.api.getSelectedRows()
        .filter((row) =>
            !selectedRawProducts.some(
                (selected) => selected.rawProductId === row.moaRawProdNo
            )
        )
        .forEach((row) => {
          setSelectedRawProducts((prevSelected) =>
              [
                ...prevSelected,
                {
                  rawProductId: row.moaRawProdNo,
                  rawProductDescription: row.moaRawProdDesc,
                  usageAmount: 0
                }
              ].sort(
                  (a, b) => (a.rawProductId > b.rawProductId ? 1 : -1)
              )
          );
        });
  }

  const onSelectedSelectionChanged = (
      event: SelectionChangedEvent
  ) => {
    setSelectedRawProducts((prevSelected) =>
        prevSelected.filter((rawProduct) =>
            event.api.getSelectedRows().some(
                (selectedRow) => selectedRow.rawProductId === rawProduct.rawProductId
            )
        )
    )
  }

  const editUsageAmount = (
      event: CellEditRequestEvent
  ) => {
    let newValue = +event.newValue;
    if (Number.isNaN(newValue)) {
      event.api.stopEditing();
      return;
    }
    newValue = Math.min(Math.max(newValue, -9999.9999), 9999.9999);
    setSelectedRawProducts((prevSelected) =>
        prevSelected.map((rawProduct) => {
          if (rawProduct.rawProductId === event.data.rawProductId) {
            return {
              ...rawProduct,
              usageAmount: newValue
            }
          }
          return rawProduct;
        })
    )
  }

  return (
      <>
        <TableWrapper>
          <Table
              ref={availableGridRef}
              headerTitle={"Available Raw Products"}
              width={'100%'}
              rowData={rawProducts}
              returnedRecords={rawProducts.length}
              rowSelection={'multiple'}
              rowMultiSelectWithClick={true}
              onSelectionChanged={onAvailableSelectionChanged}
              columnDefs={[
                {
                  width: 48,
                  checkboxSelection: true,
                  floatingFilter: false,
                  sortable: false
                },
                { minWidth: 80, flex: 1, field: 'moaRawProdNo', headerName: 'Raw Product' },
                { minWidth: 80, flex: 2, field: 'moaRawProdDesc', headerName: 'Description', resizable: false }
              ]}
          />
        </TableWrapper>
        <TableWrapper>
          <Table
              headerTitle={'Selected Raw Products'}
              width={'100%'}
              showHeader={true}
              rowData={selectedRawProducts}
              returnedRecords={selectedRawProducts.length}
              suppressRowClickSelection={true}
              onSelectionChanged={onSelectedSelectionChanged}
              readOnlyEdit={true}
              onCellEditRequest={editUsageAmount}
              columnDefs={[
                {
                  maxWidth: 48,
                  field: '',
                  headerName: '',
                  checkboxSelection: true,
                  floatingFilter: false,
                  sortable: false,
                  flex: 1
                },
                { minWidth: 48, field: 'rawProductId', headerName: 'Raw Product', flex: 1 },
                { minWidth: 48, field: 'rawProductDescription', headerName: 'Description', flex: 2 },
                {
                  minWidth: 48,
                  field: 'usageAmount',
                  headerName: 'Usage Amount',
                  editable: true,
                  cellRenderer: EditCellRenderer,
                  flex: 1,
                  resizable: false
                }
              ]}
          />
        </TableWrapper>
      </>
  )
}

export default EditRecipeDetailsTables;
